import * as pc from 'playcanvas';
import { isTouchDevice } from '../device';

export interface GameApp {
  app: pc.Application;
  keyboard: pc.Keyboard;
  camera: pc.Entity;
}

/** Boots the PlayCanvas application: canvas, input devices, lighting, camera. */
export function createGameApp(canvas: HTMLCanvasElement): GameApp {
  const keyboard = new pc.Keyboard(window);
  const app = new pc.Application(canvas, {
    keyboard,
    mouse: new pc.Mouse(canvas),
    touch: isTouchDevice ? new pc.TouchDevice(canvas) : undefined,
    graphicsDeviceOptions: {
      antialias: true,
      alpha: false,
      powerPreference: 'high-performance',
    },
  });

  app.setCanvasFillMode(pc.FILLMODE_FILL_WINDOW);
  app.setCanvasResolution(pc.RESOLUTION_AUTO);
  window.addEventListener('resize', () => app.resizeCanvas());

  // Flat placeholder sky/ambient — instant, replaced once the real HDRI streams in below.
  app.scene.ambientLight = new pc.Color(0.45, 0.5, 0.6);

  const sun = new pc.Entity('sun');
  sun.addComponent('light', {
    type: 'directional',
    color: new pc.Color(1.0, 0.95, 0.85),
    intensity: 1.4,
    castShadows: true,
    shadowDistance: 160,
    shadowResolution: 2048,
  });
  sun.setLocalEulerAngles(50, 30, 0);
  app.root.addChild(sun);

  // Cool sky-fill from the opposite azimuth so facades facing away from the sun
  // read as shaded architecture instead of dead-black slabs.
  const fill = new pc.Entity('sky-fill');
  fill.addComponent('light', {
    type: 'directional',
    color: new pc.Color(0.75, 0.82, 1.0),
    intensity: 0.45,
    castShadows: false,
  });
  fill.setLocalEulerAngles(35, 210, 0);
  app.root.addChild(fill);

  const camera = new pc.Entity('camera');
  camera.addComponent('camera', {
    clearColor: new pc.Color(0.55, 0.72, 0.92), // day sky
    fov: 60,
    farClip: 900,
  });
  camera.setPosition(0, 6, 12);
  app.root.addChild(camera);

  loadHdriSky(app);
  applyRealismPostFx(app, camera);
  applyAtmosphericFog(app);

  return { app, keyboard, camera };
}

/**
 * Cheap, asset-free realism: ACES filmic tone mapping (the flat default reads washed-out
 * against real PBR/HDRI lighting), subtle screen-space ambient occlusion to ground the
 * buildings with contact shadows, and a whisper of bloom so sky/glass highlights bleed
 * like a camera lens. Kept light (no DOF/TAA) — this runs on the mobile perf budget (§8).
 */
function applyRealismPostFx(app: pc.Application, camera: pc.Entity): void {
  if (!camera.camera) return;
  const cameraFrame = new pc.CameraFrame(app, camera.camera);
  cameraFrame.rendering.toneMapping = pc.TONEMAP_ACES;
  cameraFrame.ssao.type = pc.SSAOTYPE_LIGHTING;
  cameraFrame.ssao.intensity = 0.3;
  cameraFrame.ssao.radius = 6;
  cameraFrame.bloom.intensity = 0.02;
  cameraFrame.enabled = true;
  cameraFrame.update();
}

/** Fades distant geometry into the sky color instead of the world abruptly stopping at its edge. */
function applyAtmosphericFog(app: pc.Application): void {
  app.scene.fog.type = pc.FOG_EXP2;
  app.scene.fog.color = new pc.Color(0.75, 0.8, 0.85);
  app.scene.fog.density = 0.0016;
}

/**
 * Streams in a real HDRI sky (CC0, public/hdri/LICENSE.txt) over the flat placeholder sky/ambient —
 * a skybox background plus image-based ambient/reflection lighting for every PBR material in the
 * scene (ground, kit buildings, houses). Fire-and-forget: cold boot never waits on it.
 */
function loadHdriSky(app: pc.Application): void {
  const asset = new pc.Asset('sky-hdri', 'texture', { url: '/hdri/sky.hdr' }, { mipmaps: false });
  asset.once('load', () => {
    const hdri = asset.resource as pc.Texture;

    app.scene.skybox = pc.EnvLighting.generateSkyboxCubemap(hdri);
    app.scene.sky.type = pc.SKYTYPE_INFINITE;

    const lightingSource = pc.EnvLighting.generateLightingSource(hdri);
    app.scene.envAtlas = pc.EnvLighting.generateAtlas(lightingSource);
    lightingSource.destroy();
  });
  asset.once('error', (err: unknown) => console.warn('[GOC] failed to load HDRI sky:', err));
  app.assets.add(asset);
  app.assets.load(asset);
}

/** Standard opaque colored material used by the procedural builders. */
export function colorMaterial(hex: string, emissive = false): pc.StandardMaterial {
  const mat = new pc.StandardMaterial();
  const color = new pc.Color();
  color.fromString(hex);
  mat.diffuse = color;
  if (emissive) {
    mat.emissive = color;
    mat.emissiveIntensity = 0.6;
  }
  mat.update();
  return mat;
}

/** Creates a box entity with a colored material at a world position. */
export function box(
  name: string,
  size: { x: number; y: number; z: number },
  position: { x: number; y: number; z: number },
  material: pc.StandardMaterial,
): pc.Entity {
  const e = new pc.Entity(name);
  e.addComponent('render', { type: 'box' });
  if (e.render) {
    e.render.material = material;
  }
  e.setLocalScale(size.x, size.y, size.z);
  e.setPosition(position.x, position.y, position.z);
  return e;
}
